// src/pages/Order/Sales Order/Sales Order Entry/components/CreateSalesOrderForm.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Form,
  Grid,
  Button,
  DatePicker,
  Typography,
  Flex,
  Radio,
  Select,
  InputNumber,
  Input,
  Checkbox,
  message,
  Drawer,
  Tooltip,
} from "antd";
import { useForm } from "antd/es/form/Form";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import InputField from "../../../../../components/Input/Input";
import SearchListAutoComplete from "../../../../../components/SearchListAutoComplete";

import {
  fetchCustomers,
  fetchItems,
  fetchSingleInstitutionDetails,
  fetchTagGroupFields,
} from "../../../../../store/commonThunks/lookups";
import { fetchSalesOrderNumber, createSalesOrder, fetchSingleSalesOrder } from "../salesOrderThunk";
import { dateTimeFormat, formatToCompanyTimezone, getYearFromCompanyDate } from "../../../../../lib/functions";
import {
  resetSalesOrder,
  setCustomer,
  setEntryDate,
  setDueDate,
  addOrderRow,
  deleteOrderRow,
  setTaxMode,
  setTagFieldId,
  setFormField,
  setPaymentTerm,
  setPoReference,
  calculateTotals,
  loadQuotationForSalesOrder,
  setConversionRate,
  setConversion,
  clearOrderForCustomerChange,
  loadSalesOrderForEdit,
} from "../salesOrderSlice";
import InputNumberField from "../../../../../components/Input/InputNumber";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import { fetchQuotationWithDetails } from "../../../../QuotationRecord/QuotationRecordThunk";
import { getCurrencyCodeWithName } from "../../../../../lib/currency";
import { convertCurrency } from "../../../../../utils";
import CustomerDrawer from "../../../../Customers/components/CustomerDrawer";
import AddNewModal from "../../../../../components/DropDown/AddNewModal";
import TagCreateForm from "../../../../Settings/Tags/components/TagCreateForm";
import useMigrationDate from "../../../../../hooks/useMigrationDate";
import { QuestionCircleOutlined } from "@ant-design/icons";

const { Title } = Typography;
const { useBreakpoint } = Grid;

const CreateSalesOrderForm = ({ quoteId, edit_sale_o_id }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [form] = useForm();
  const lookups = useSelector((s) => s.lookups);
  const authInfo = useSelector(selectAuthInfo);


  const {
    customerInfo,
    voucherNumber,
    paymentTerm,
    entryDate,
    poReference,
    dueDays,
    paymentTermsText,
    itemCart,
    conversionRate,
  } = useSelector((s) => s.salesOrder);


  const [isConvertCurrencyShow, SetIsConvertCurrencyShow] = useState(false);
  const [customerDrawerOpen, setCustomerDrawerOpen] = useState(false);
  const [tagModal, setTagModal] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const { tagFieldId, taxMode } = useSelector((s) => s.salesOrder);

  const currency = authInfo?.currency || "AED";
  const isFieldHidden = (fieldKey, pageKey = "sales_order_entry") => {
    return authInfo?.visibilitytabs?.some(
      (item) => item.page_key === pageKey && item.field_key === fieldKey
    );
  };

  useEffect(() => {
    if (edit_sale_o_id) return;
    let formatedDate = entryDate ? dayjs(entryDate).toISOString() : null;
    let companydate = formatToCompanyTimezone(formatedDate);
    const year = getYearFromCompanyDate(companydate);
    dispatch(fetchSalesOrderNumber(year));
  }, [entryDate]);

  useEffect(() => {
    dispatch(fetchCustomers(""));
    dispatch(fetchItems(""));
    dispatch(fetchTagGroupFields(""));
   dispatch(fetchSingleInstitutionDetails());
  }, [dispatch]);


  useEffect(() => {
    if (!edit_sale_o_id) return;

    dispatch(fetchSingleSalesOrder(edit_sale_o_id))
      .unwrap()
      .then((order) => {
        dispatch(loadSalesOrderForEdit(order));
      });
  }, [edit_sale_o_id]);




  useEffect(() => {
    if (!quoteId) return;

    (async () => {
      const res = await dispatch(fetchQuotationWithDetails(quoteId)).unwrap();
      const q = res[0];

      // ✅ Map service expenses from quotation
      const serviceCart = (q.otherServiceTrans || []).map((t, idx) => ({
        key: `expense_${idx}`,
        to_acc_id: t.to_acc_id,
        to_acc_name: t.to_acc_name,
        tran_amount: parseFloat(t.tran_amount) || 0,
        acc_type_id: t.acc_type_id || "direct_income",
      }));

      // ✅ Pass serviceCart explicitly into the load action
      dispatch(loadQuotationForSalesOrder({ ...q, serviceCart }));

      const customerObj = {
        acc_id: q.acc_id,
        acc_name: q.acc_name,
        acc_code: q.acc_code,
        display_text: q.acc_name,
        address: q.address,
        contact_no: q.contact_no,
        institution_name: q.institution_name,
        credit_day: q.credit_day,
      };

      handleCustomerSelect(customerObj);

      form.setFieldsValue({
        search_customer: {
          value: customerObj.display_text,
          label: customerObj.display_text,
          raw: customerObj,
        },
      });
    })();
  }, [quoteId, dispatch, form]);

  const tagOptions = (lookups.tagFields || []).map((tag) => ({
    label: tag.display_text,
    value: tag.id, // IMPORTANT: value must be id (not display_text)
  }));

  const handleCustomerSelect = (cust) => {
    if (cust === "add_new") {
      setCustomerDrawerOpen(true);
      return;
    }

    if (!cust) return;

    if (cust) {
      dispatch(setCustomer(cust));

      // Prevent reset if conversion mode
      const isConversion = !!quoteId;

      //add one row if no item row exist
      if (!isConversion) {
        dispatch(clearOrderForCustomerChange());

        if (itemCart.length === 0) {
          dispatch(
            addOrderRow({
              key: Date.now(),
              item_id: null,
              product: "",
              quantity: 1,
              rate: 0,
              tax_per: 0,
              discount: 0,
              discountAmt: null,
              discountMode: "percent",
              bonus: false,
              item_description: "",
              showDescription: false,
            })
          );
        }
      }
    }
  };

  const handleSubmit = () => {
    if (itemCart.length === 0) {
      Swal.fire("Item cart is Empty!", "", "warning");
      return;
    }
    if (!customerInfo || !customerInfo.acc_id) {
      Swal.fire("Select customer!", "", "warning");
      return;
    }

    Swal.fire({
      title: "Saving Sales Order...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    dispatch(createSalesOrder())
      .unwrap()
      .then((res) => {
        Swal.close();
        if (!res.error) {
          Swal.fire({
            title: `${res.message}. Do you want to view invoice?`,
            icon: "success",
            showCancelButton: true,
            confirmButtonText: "Yes, view invoice",
            cancelButtonText: "No, go to sales",
          }).then((result) => {
            if (result.isConfirmed) {
              navigate(`/order/quick-sales-order-voucher/${res.sale_o_id}`);
              dispatch(resetSalesOrder());
            } else {
              dispatch(resetSalesOrder());
              navigate(`/sales`);
            }
          });
        } else {
          Swal.fire(res.message || "Something went wrong", "", "warning");
        }
      })
      .catch(() => {
        Swal.close();
        Swal.fire("Network ERROR", "", "error");
      });
  };
const {disabledDate} = useMigrationDate();
  return (
    <div>
      <Flex justify="space-between" align="center">
        <Title level={3}>
          {quoteId ? "Quotation to Order Conversion" : "Sales Order"}
        </Title>

        <Tooltip title="Help">
          <Button
            type="text"
            icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
            onClick={() => setHelpOpen(true)}
          />
        </Tooltip>
      </Flex>
      <Form
        form={form}
        layout="horizontal"
        style={{ width: "100%", maxWidth: "1200px" }}
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
      >
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          {/* Customer Info */}
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            <Form.Item label="Search Customer" required>
              <SearchListAutoComplete
                entityType="customer"
                placeholder="Customer"
                optionsList={lookups.customers.map((c) => ({
                  sale_id: c.acc_id,
                  display_text: c.acc_name || c.institution_name,
                  raw: c,
                }))}
                value={
                  customerInfo?.acc_id
                    ? {
                      value: customerInfo?.acc_name,
                      label: customerInfo?.acc_name,
                      display_text: customerInfo?.acc_name,
                      raw: customerInfo,
                    }
                    : null
                }
                onSelect={(obj) => handleCustomerSelect(obj?.raw ? obj?.raw : obj)}
                showAddNew={true}
              />
            </Form.Item>
            <Form.Item label="Institution Name">
              <InputField value={customerInfo?.institution_name} disabled />
            </Form.Item>
            <Form.Item label="Customer Address">
              <InputField value={customerInfo?.address} disabled />
            </Form.Item>
            <Form.Item label="Contact No">
              <InputField value={customerInfo?.contact_no} disabled />
            </Form.Item>

              <Form.Item label="Discount">
                <Radio.Group
                  options={[
                    {
                      label: "On Total",
                      value: "on_total",
                      "data-testid": "radio-on-total",
                    },
                    { label: "On Individual Item", value: "on_item" },
                  ]}
                  onChange={(e) => {
                    dispatch(setTaxMode(e.target.value));
                    dispatch(calculateTotals({ authInfo }));
                  }}
                  value={taxMode}
                />
              </Form.Item>

            <Form.Item label="Tags">
              <Select
                className="tag-multiselect"
                maxTagCount="responsive"
                mode="multiple"
                placeholder="Select Tags"
                options={tagOptions}
                value={tagFieldId}          // always array now
                onChange={(ids) => dispatch(setTagFieldId(ids))}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: 8,
                        borderTop: "1px solid #f0f0f0",
                      }}
                    >
                      <Button type="link" onClick={() => setTagModal(true)}>
                        + Add New Tag
                      </Button>
                    </div>
                  </>
                )}
              />
            </Form.Item>

          </Flex>

          {/* Order Info */}
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            <Form.Item label="Order No">
              <InputField
                value={voucherNumber || ""}
                disabled
                data-testid="order-no"
              />
            </Form.Item>
            <Form.Item label="Entry Date" required>
              <DatePicker
                style={{ width: "100%" }}
                value={entryDate ? dayjs(entryDate) : null}
                onChange={(d) =>
                  dispatch(setEntryDate(d ? d.toISOString() : null))
                }
                format={dateTimeFormat}
                           disabledDate={disabledDate}
              />
            </Form.Item>
            <Form.Item label="PO Reference">
              <Input
                value={poReference || ""}
                onChange={(e) => {
                  dispatch(setPoReference(e.target.value));
                }}
              />
            </Form.Item>
            <Form.Item label="Due Days">
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                onWheel={(e) => e.target.blur()}
                value={paymentTerm}
                onChange={(value) => {
                  dispatch(setPaymentTerm(value));
                  dispatch(setFormField({ field: "dueDays", value }));

                  if (!value || value == 0) {
                    dispatch(
                      setFormField({
                        field: "paymentTerms",
                        value: "Immediate",
                      })
                    );
                  } else if (value == 1) {
                    dispatch(
                      setFormField({ field: "paymentTerms", value: "Next Day" })
                    );
                  } else {
                    dispatch(
                      setFormField({
                        field: "paymentTerms",
                        value: `${value} Days`,
                      })
                    );
                  }
                }}
              />
            </Form.Item>

            <Form.Item label="Payment Terms">
              <Input value={paymentTermsText} disabled />
            </Form.Item>
            {!isFieldHidden("Convert Currency") && (
              <Form.Item>
                <Checkbox
                  onChange={(e) => {
                    const checked = e.target.checked;

                    SetIsConvertCurrencyShow(checked);

                    if (!checked) {
                      // 🔥 Reset conversion in Redux
                      dispatch(setConversionRate(0));
                      dispatch(setConversion(null, "currency"));
                    }
                  }}
                >
                  Convert Currency
                </Checkbox>
              </Form.Item>
            )}
          </Flex>
        </Flex>
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          {isConvertCurrencyShow && (
            <>
              <Flex
                vertical
                gap={10}
                style={{
                  width: !screens.md || !screens.xl ? "100%" : "40%",
                }}
              >
                <Form.Item label="Choose Currency">
                  <Select
                    showSearch
                    placeholder="Select Currency"
                    style={{ minWidth: 200 }}
                    options={getCurrencyCodeWithName().map((c) => ({
                      value: c.code,
                      label: `${c.currency} (${c.code})`,
                    }))}
                    onChange={async (toCurrency) => {
                      const rate = await convertCurrency(currency, toCurrency);
                      if (rate) {
                        // store conversion rate once for use in InvoiceTable
                        dispatch(setConversion(toCurrency, "currency"));

                        dispatch(setConversionRate(rate));
                      }
                    }}
                  />
                </Form.Item>
              </Flex>
              <Flex
                vertical
                gap={10}
                style={{
                  width: !screens.md || !screens.xl ? "100%" : "40%",
                }}
              >
                <Form.Item label="Conversion Rate">
                  <InputField value={conversionRate} disabled />
                </Form.Item>
              </Flex>
            </>
          )}
        </Flex>
        {/* <Flex gap={16}>
          <Button onClick={() => dispatch(resetSalesOrder())}>Reset</Button>
          <Button type="primary" onClick={handleSubmit}>
            Save
          </Button>
        </Flex> */}
      </Form>

      <CustomerDrawer
        open={customerDrawerOpen}
        onClose={() => setCustomerDrawerOpen(false)}
        authInfo={authInfo}
        onCreated={(customer) => {
          if (!customer) return;

          const customerObj = {
            sale_id: customer.acc_id,
            display_text: customer.acc_name || customer.institution_name,
            raw: customer,
          };

          setCustomerDrawerOpen(false);

          dispatch(fetchCustomers());
          // 3️⃣ Update Redux
          dispatch(setCustomer(customer));


        }}
      />
      <AddNewModal
        open={tagModal}
        title="Add Tag"
        onClose={() => setTagModal(false)}
      >
        <TagCreateForm
          authInfo={authInfo}
          onSuccess={async ({ groupId, createdName }) => {
            const res = await dispatch(fetchTagGroupFields("")).unwrap();

            const match = res.find(
              (t) =>
                t?.raw?.field_name?.toLowerCase() ===
                createdName.toLowerCase()
            );

            if (!match) {
              message.warning("Tag creation failed or tag not found");
              setTagModal(false);
              return;
            }

            if (tagFieldId.includes(match.id)) {
              message.info("Tag already exists and is already selected");
              setTagModal(false);
              return;
            }

            // ✅ New tag OR existing but not selected
            dispatch(setTagFieldId([...tagFieldId, match.id]));
            message.success("Tag added");

            setTagModal(false);
          }}
        />

      </AddNewModal>
      <Drawer
        title="Sales Order Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Sales Order Entry
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Sales Order Entry module is used to create and manage customer
            sales orders before invoice generation. It helps businesses track
            customer orders, pricing, delivery details, and sales workflow
            efficiently within the ERP system.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Steps to Create Sales Order</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>Open <strong>Order → Sales Order</strong></li>
              <li>Select or create the customer</li>
              <li>Verify order number and entry date</li>
              <li>Add products/items with quantity and pricing</li>
              <li>Apply discounts if required</li>
              <li>Set payment terms and PO reference if needed</li>
              <li>Review order totals and customer details</li>
              <li>Click <strong>Save</strong> to create the sales order</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Customer selection is required before saving.</li>
              <li>Order totals change based on VAT and discount settings.</li>
              <li>Currency conversion can be enabled when required.</li>
              <li>Saved sales orders can later be converted into invoices.</li>
            </ul>
          </div>
        </div>
       </Drawer>
    </div>
  );
};

export default CreateSalesOrderForm;
